import {
  Button,
  CopySVG,
  FlagHollowSVG,
  Toast,
  useModal,
} from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { filter, maxBy } from "lodash";
import { useMemo, useState } from "react";

import globalToasterRef from "@/base/globalToasterRef";
import { type IStandard } from "@/search/Langston/types/IStandard";
import TT_AND_AUTO_WP_COPY from "@/view/constants/teacherToolsAndAutoGenWritePrompts";
import LessonPlannerTool from "@/view/ContentDisplay/TeacherTools/LessonPlannerTool";
import { AllottedTimeValue } from "@/view/ContentDisplay/TeacherTools/LessonPlannerTool/AllottedTimeButtonGroup";
import useTeacherToolsGet from "@/view/ContentDisplay/TeacherTools/useTeacherToolsGet";
import ReportContentFormModal from "@/view/ReportContentFormModal";
import { isArticle } from "@/view/utils/contentTypeChecks";
import useViewPageContentContext from "@/view/ViewPageContent/useViewPageContentContext";
import {
  actionButtonsCss,
  algorithmOutputCss,
  bodyPaddingCss,
  disclaimerCss,
} from "./TeacherToolsContent.style";

interface TeacherToolsContentProps {
  selectedStandards: IStandard[];
  selectedTool: string;
  setIsStandardsModalActive: (active: boolean) => void;
  setSelectedStandards: React.Dispatch<React.SetStateAction<IStandard[]>>;
}

const ButtonWithTrackEvent = withTrackEvent(Button);

const TeacherToolsContent = ({
  selectedStandards,
  selectedTool,
  setIsStandardsModalActive,
  setSelectedStandards,
}: TeacherToolsContentProps) => {
  const { content, currentLevel } = useViewPageContentContext();

  const contentData = content?.data;
  const isArticleData = isArticle(contentData);

  // Load teacher tools for the max article level (in the current language)
  // instead of the current level to save API costs. The only
  // exception to this is key-terms, where the extracted terms should be
  // findable in the text.
  const assumedLevel = useMemo(() => {
    const levelsForCurrentLanguage = filter(
      isArticleData ? contentData?.article_levels : [],
      (level) => level.language === currentLevel?.language,
    );

    if (selectedTool === "key-terms" || levelsForCurrentLanguage.length === 1) {
      return currentLevel;
    }

    return maxBy(levelsForCurrentLanguage, "raw_lexile");
  }, [selectedTool, currentLevel, contentData, isArticleData]);

  const [reportContentModalProps, setReportContentModalActive, triggerRef] =
    useModal(false);

  const [allottedTime, setAllottedTime] = useState<AllottedTimeValue>(60);
  const [learningObjective, setLearningObjective] = useState("");
  const [supportingMaterials, setSupportingMaterials] = useState({
    includeProjectIdea: false,
    includeMultilingualLearners: false,
    includeAssessmentQuestions: false,
  });

  // Skip asset generation for lesson planner, this is handled separately.
  const isLessonPlannerToolSelected = selectedTool === "lesson-planner";
  const {
    data: generatedTextAsset,
    loading: loadingGeneratedTextAsset,
    error: loadError,
  } = useTeacherToolsGet({
    content,
    selectedTool,
    assumedLevel,
    skip: isLessonPlannerToolSelected,
  });

  const streamProperties = {
    component_type: "TeacherTools",
    content_id: assumedLevel?.id || "",
    content_type: "ArticleLevel",
    object_id: generatedTextAsset?.content_id,
    object_type: objectTypes.generatedTextAsset,
  };

  const formatGeneratedText = useMemo((): string => {
    if (loadingGeneratedTextAsset) return "Loading...";

    if (loadError)
      return "Unfortunately, we've run into a problem trying to generate this. Please try again later.";

    if (!generatedTextAsset?.algorithm_output)
      return "Select from the list above to generate a response in seconds! 🌟💫";

    return generatedTextAsset?.algorithm_output;
  }, [
    loadingGeneratedTextAsset,
    loadError,
    generatedTextAsset?.algorithm_output,
  ]);

  const onCopyClick = () => {
    navigator.clipboard.writeText(generatedTextAsset.algorithm_output);
    globalToasterRef.current.displayToast({
      showIcon: true,
      statusColor: Toast.statusColor.success,
      children: "Copied to clipboard",
    });
  };

  if (selectedTool === "lesson-planner") {
    return (
      <LessonPlannerTool
        contentId={content?.id || ""}
        currentLevel={currentLevel}
        learningObjective={learningObjective}
        setLearningObjective={setLearningObjective}
        selectedStandards={selectedStandards}
        setSelectedStandards={setSelectedStandards}
        supportingMaterials={supportingMaterials}
        setSupportingMaterials={setSupportingMaterials}
        allottedTime={allottedTime}
        setAllottedTime={setAllottedTime}
        setStandardsModalActive={setIsStandardsModalActive}
      />
    );
  }

  return (
    <>
      <div css={algorithmOutputCss}>{formatGeneratedText}</div>
      {generatedTextAsset && !loadingGeneratedTextAsset && (
        <div css={bodyPaddingCss}>
          <div css={disclaimerCss}>{TT_AND_AUTO_WP_COPY.DISCLAIMER}</div>
          <div css={actionButtonsCss}>
            <ButtonWithTrackEvent
              icon={{
                SvgComponent: CopySVG,
                alignment: Button.iconAlignments.left,
              }}
              flavor={Button.flavors.muted}
              onClick={onCopyClick}
              actionName={actionNames.copy}
              objectType={streamProperties.object_type}
              streamProperties={{
                ...streamProperties,
                description: "Copy to clipboard",
              }}
              dataProps={{ "data-qa-selector": "teacher_tools_copy_text" }}
            >
              Copy
            </ButtonWithTrackEvent>
            <ButtonWithTrackEvent
              icon={{
                SvgComponent: FlagHollowSVG,
                alignment: Button.iconAlignments.left,
              }}
              flavor={Button.flavors.tertiary}
              onClick={() => setReportContentModalActive(true)}
              ref={triggerRef}
              actionName={actionNames.flag}
              objectType={streamProperties.object_type}
              streamProperties={{
                ...streamProperties,
                description: "Report Content",
              }}
              dataProps={{ "data-qa-selector": "teacher_tools_flag" }}
            >
              Report content
            </ButtonWithTrackEvent>
          </div>
          <ReportContentFormModal
            contentId={generatedTextAsset.content_id}
            reportContentModalProps={reportContentModalProps}
            setReportContentModalActive={setReportContentModalActive}
            reportSource={"TeacherTools"}
          />
        </div>
      )}
    </>
  );
};

export default TeacherToolsContent;
